<template>
  <div ref="container" class="google-places-autocomplete"></div>
</template>

<script>
  // Field -> text variant mapping, mirroring the legacy vue-google-autocomplete
  // ADDRESS_COMPONENTS config so getAddressData() in RequestForm keeps receiving
  // the same shape of values it used to get from the old Autocomplete widget.
  const ADDRESS_FIELD_VARIANT = {
    route: 'longText',
    street_number: 'shortText',
    postal_code: 'shortText',
    locality: 'longText',
    postal_town: 'longText',
    administrative_area_level_1: 'shortText',
  };

  // Thin wrapper around google.maps.places.PlaceAutocompleteElement (Places API New),
  // replacing the legacy google.maps.places.Autocomplete (vue-google-autocomplete).
  export default {
    name: 'GooglePlacesAutocomplete',

    props: {
      country: {
        type: Array,
        default: () => [],
      },
      placeholder: {
        type: String,
        default: '',
      },
      id: {
        type: String,
        required: true,
      },
    },

    data() {
      return {
        element: null,
        isDestroyed: false,
      };
    },

    watch: {
      country(newCountry) {
        if (this.element) {
          this.element.includedRegionCodes = newCountry;
        }
      },
    },

    async mounted() {
      await this.waitForPlacesLibrary();
      if (this.isDestroyed) return;
      this.createElement();
    },

    beforeDestroy() {
      this.isDestroyed = true;
      if (this.element) {
        this.element.removeEventListener('gmp-select', this.handlePlaceSelect);
      }
    },

    methods: {
      async waitForPlacesLibrary() {
        if (window.google && window.google.maps && typeof window.google.maps.importLibrary === 'function') {
          await window.google.maps.importLibrary('places');
          return;
        }
        // Fallback for the classic bootstrap script (public/index.html) in case
        // importLibrary isn't exposed: poll until the global becomes available.
        await new Promise(resolve => {
          const check = () => {
            if (this.isDestroyed) {
              resolve();
              return;
            }
            const ready = window.google && window.google.maps && window.google.maps.places
              && window.google.maps.places.PlaceAutocompleteElement;
            ready ? resolve() : setTimeout(check, 100);
          };
          check();
        });
      },

      createElement() {
        if (!window.google || !window.google.maps || !window.google.maps.places) return;
        this.element = new window.google.maps.places.PlaceAutocompleteElement({
          includedRegionCodes: this.country,
        });
        this.element.id = this.id;
        // Own class name applied directly to the custom element so it can be
        // styled from App.vue via `.google-autocomplete::part(input)`.
        this.element.classList.add('google-autocomplete');
        if (this.placeholder) {
          this.element.placeholder = this.placeholder;
        }
        this.element.addEventListener('gmp-select', this.handlePlaceSelect);
        this.$refs.container.appendChild(this.element);
      },

      async handlePlaceSelect(event) {
        const place = event.placePrediction.toPlace();
        await place.fetchFields({fields: ['addressComponents', 'formattedAddress', 'location']});
        const normalized = this.normalizeAddressComponents(place.addressComponents);
        this.$emit('placechanged', normalized, place, this.id);
      },

      normalizeAddressComponents(components) {
        const normalized = {};
        (components || []).forEach(component => {
          component.types.forEach(type => {
            const variant = ADDRESS_FIELD_VARIANT[type];
            if (variant && normalized[type] === undefined) {
              normalized[type] = component[variant];
            }
          });
        });
        return normalized;
      },

      // Resets the field for clearSearch()/swapAddress() in RequestForm.
      clear() {
        if (!this.element) return;
        // PlaceAutocompleteElement has no documented setter for the raw typed text,
        // so reset it the same way a user would: through its "input" shadow part
        // (the same part exposed for ::part(input) styling).
        const input = this.element.shadowRoot && this.element.shadowRoot.querySelector('[part="input"]');
        if (input) {
          input.value = '';
          input.dispatchEvent(new Event('input', {bubbles: true}));
        }
        this.element.value = '';
      },
    },
  };
</script>
